import { useEffect, useMemo } from "react";
// routing
import Routes from "routes";

// project imports
import NavigationScroll from "layout/NavigationScroll";
import ThemeCustomization from "themes";
import Locales from "ui-component/Locales";
import RTLLayout from "ui-component/RTLLayout";
import Snackbar from "ui-component/extended/Snackbar";

// auth provider
import { BrandingProvider } from "contexts/BrandingContext";
import { FirebaseProvider as AuthProvider } from "contexts/FirebaseContext";
import { NegocioProvider } from "contexts/NegocioContext";
// import { AWSCognitoProvider as AuthProvider } from 'contexts/AWSCognitoContext';
// import { JWTProvider as AuthProvider } from 'contexts/JWTContext';
// import { Auth0Provider as AuthProvider } from 'contexts/Auth0Context';

import { getCurrentBrandConfig } from "config/brandConfig";
import { useBranding } from "contexts/BrandingContext";
import { useNegocioContext } from "contexts/NegocioContext";
import { Helmet } from "react-helmet";
import { initializeBranding, updateBrandingForNegocio } from "utils/brandingInit";
import { updateBrandConfig } from "utils/themeUtils";

// Componente que maneja la integración entre Negocio y Branding
// NOTA: Con WHITE_LABEL_ID en .env, el branding ya no depende del negocio logueado
const BrandingNegocioIntegration = ({ children }) => {
  const { negocio } = useNegocioContext();
  const { branding } = useBranding();

  useEffect(() => {
    // Solo actualizar branding si no hay WHITE_LABEL_ID configurado y el negocio cambia
    // Con WHITE_LABEL_ID, el branding es independiente del negocio logueado
    const WHITE_LABEL_ID = process.env.REACT_APP_WHITE_LABEL_ID;

    if (!WHITE_LABEL_ID && negocio?.id && (!branding || branding.negocio_id !== negocio.negocio_id)) {
      updateBrandingForNegocio(negocio.negocio_id).catch((error) => {
        console.error("Failed to update branding for negocio:", error);
      });
    }
  }, [negocio?.id, negocio?.negocio_id, branding]);

  return children;
};

// Componente interno que usa el contexto de branding
const AppContent = () => {
  const { branding, loading } = useBranding();

  // Configuración dinámica basada en branding
  const appConfig = useMemo(() => {
    return branding
      ? {
          title: `${branding.white_label?.name || "Rhino"} - Dashboard Administración`,
          description: `Comience a aumentar sus ventas con ${branding.white_label?.name || "Rhino"}.`,
          favicon: branding.branding?.favicon_url || "/favicon.png",
          siteName: branding.white_label?.siteName || "rhinox.io",
          url: branding.white_label?.url || "https://rhinox.io/",
          ogImage: branding.branding?.og_image || "https://rhinox.io/og-image/og-facebook.png",
          twitterImage: branding.branding?.twitter_image || "https://rhinox.io/og-image/og-twitter.png",
          themeColor: branding.branding?.primary_color || "#2296f3",
          brandDescription: `${branding.white_label?.name || "Rhino"} administración, la mejor forma de vender tus productos`,
        }
      : getCurrentBrandConfig(); // Fallback a configuración estática
  }, [branding]);

  useEffect(() => {
    // Actualizar configuración de marca dinámicamente solo si no está cargando
    if (!loading && appConfig) {
      updateBrandConfig(appConfig);
    }
  }, [appConfig, loading]);

  // Mostrar loading básico mientras se carga el branding
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "16px",
          color: "#666",
        }}>
        Cargando configuración...
      </div>
    );
  }

  return (
    <ThemeCustomization>
      <Helmet>
        <title>{appConfig.title}</title>
        <meta name="title" content={appConfig.title} />
        <meta name="description" content={appConfig.description} />
        <meta name="theme-color" content={appConfig.themeColor} />
        <meta property="og:title" content={appConfig.title} />
        <meta property="og:description" content={appConfig.brandDescription} />
        <meta property="og:url" content={appConfig.url} />
        <meta property="og:site_name" content={appConfig.siteName} />
        <meta property="og:image" content={appConfig.ogImage} />
        <meta property="twitter:url" content={appConfig.url} />
        <meta property="twitter:title" content={appConfig.title} />
        <meta property="twitter:description" content={appConfig.brandDescription} />
        <meta property="twitter:image" content={appConfig.twitterImage} />
      </Helmet>
      {/* RTL layout */}
      <RTLLayout>
        <Locales>
          <NavigationScroll>
            <NegocioProvider>
              <BrandingNegocioIntegration>
                <AuthProvider>
                  <Routes />
                  <Snackbar />
                </AuthProvider>
              </BrandingNegocioIntegration>
            </NegocioProvider>
          </NavigationScroll>
        </Locales>
      </RTLLayout>
    </ThemeCustomization>
  );
};

// ==============================|| APP ||============================== //

const App = () => {
  useEffect(() => {
    // Inicializar branding al arrancar la aplicación
    initializeBranding().catch((error) => {
      console.error("Failed to initialize branding on startup:", error);
    });
  }, []); // Solo ejecutar una vez al montar la aplicación

  return (
    <BrandingProvider>
      <AppContent />
    </BrandingProvider>
  );
};

export default App;
